import { type FC, useMemo } from "react";
import { motion } from "motion/react";
import { cn } from "utils";
import {
  introEaseOut,
  introFadeUp,
  introStaggerContainer,
} from "../introMotionPresets";
import { createSignInIntroCopy } from "../signInIntroCopy";
import type { IntroHeroProps } from "../types";
import { useIntroIsActive } from "../IntroActiveIndexContext";
import { useParams } from "next/navigation";

type IntroSlide4Layout = "mobile" | "desktop";

interface IntroSlide4ActivityProps extends Omit<IntroHeroProps, "isActive"> {
  /** H5 居中卡片 / PC 右栏视觉层 */
  layout?: IntroSlide4Layout;
}

export const IntroSlide4Activity: FC<IntroSlide4ActivityProps> = ({
  layout = "mobile",
}) => {
  const isActive = useIntroIsActive(3);
  const { locale = "en" } = useParams() as { locale?: string };
  const copy = useMemo(() => createSignInIntroCopy(locale).intro4, [locale]);
  const isDesktop = layout === "desktop";

  return (
    <motion.div
      className={cn(
        "flex w-full flex-col gap-2.5 rounded-xl p-4",
        isDesktop
          ? "max-w-[448px] gap-4 rounded-[19px] bg-[rgba(1,38,43,0.9)] p-6"
          : "mx-auto max-w-[280px] bg-[rgba(1,38,43,0.8)]",
      )}
      data-testid="intro-slide-4-activity"
      data-node-id={isDesktop ? "96344:109955" : "96105:27402"}
      data-layout={layout}
      initial="hidden"
      animate={isActive ? "visible" : "hidden"}
      variants={introFadeUp}
      transition={introEaseOut}
    >
      <div className={cn("flex items-center gap-3", isDesktop && "gap-[19px]")}>
        <span
          className={cn(
            "shrink-0 tabular-nums text-content_aura_lv3_primary",
            isDesktop
              ? "text-[58px] font-semibold leading-[70px]"
              : "text-[36px] font-semibold leading-[44px]",
          )}
        >
          {copy.statNumber}
        </span>
        <div className="flex min-w-0 flex-col">
          <p
            className={cn(
              isDesktop
                ? "text-[22px] font-semibold leading-8 text-content_inverse_primary"
                : "title_m text-content_inverse_primary",
            )}
          >
            {copy.statLine1}
          </p>
          <p
            className={cn(
              isDesktop
                ? "text-[19px] font-medium leading-[26px] text-content_inverse_tertiary"
                : "body_medium_s text-content_inverse_tertiary",
            )}
          >
            {copy.statLine2}
          </p>
        </div>
      </div>

      <div
        className="h-px w-full shrink-0 bg-background_white_overlay15"
        aria-hidden
      />

      <motion.ul
        className={cn("flex flex-col", isDesktop ? "gap-4" : "gap-2.5")}
        variants={introStaggerContainer}
        initial="hidden"
        animate={isActive ? "visible" : "hidden"}
      >
        {copy.bullets.map((bullet) => (
          <motion.li
            key={bullet}
            variants={introFadeUp}
            transition={introEaseOut}
            className={cn(
              "flex items-center",
              isDesktop ? "gap-[19px]" : "gap-3",
            )}
          >
            <span
              className={cn(
                "shrink-0 rounded-full bg-content_aura_lv3_primary",
                isDesktop ? "size-3" : "size-2",
              )}
              aria-hidden
            />
            <span
              className={cn(
                isDesktop
                  ? "text-[22px] leading-8 text-content_inverse_primary"
                  : "body_m text-content_inverse_primary",
              )}
            >
              {bullet}
            </span>
          </motion.li>
        ))}
      </motion.ul>
    </motion.div>
  );
};
